// TEMPORARY: Android Tester Recruitment - Remove after 8 testers
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Modal for recruiting Android app testers
 * Shows a form to collect name and email for Google Play Store testing
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Called when user closes/dismisses modal
 * @param {function} onSuccess - Called after successful submission
 * @param {string} playerName - Pre-fill name from game
 */
const AndroidTesterModal = ({ isOpen, onClose, onSuccess, playerName }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: playerName || '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', 'full', 'duplicate', null
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (submitStatus === 'error') {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.name.trim() || !formData.email.trim()) {
      setSubmitStatus('error');
      setErrorMessage(t('androidTester.errorRequired'));
      return;
    }

    if (!validateEmail(formData.email.trim())) {
      setSubmitStatus('error');
      setErrorMessage(t('androidTester.errorInvalidEmail'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const serverUrl = process.env.REACT_APP_API_URL || localStorage.getItem('server_url') || 'http://localhost:4001';
      const response = await fetch(`${serverUrl}/api/android-testers/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim()
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        // Mark as submitted in localStorage
        localStorage.setItem('qwixx_android_tester_prompted', 'submitted');
        // Notify parent after short delay to show success message
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 3000);
      } else {
        // Handle specific error types
        if (result.error === 'recruitment_full') {
          setSubmitStatus('full');
          localStorage.setItem('qwixx_android_tester_prompted', 'recruitment_closed');
        } else if (result.error === 'duplicate_email') {
          setSubmitStatus('duplicate');
        } else {
          setSubmitStatus('error');
          setErrorMessage(result.message || t('androidTester.errorGeneric'));
        }
      }
    } catch (error) {
      console.error('Error submitting android tester signup:', error);
      setSubmitStatus('error');
      setErrorMessage(t('androidTester.errorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('qwixx_android_tester_prompted', 'skipped');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        padding: '0',
        maxWidth: '420px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Android-themed header */}
        <div style={{
          background: 'linear-gradient(135deg, #3ddc84 0%, #1a73e8 100%)',
          padding: '1.5rem',
          borderRadius: '1rem 1rem 0 0',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🤖</div>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            margin: 0
          }}>
            {t('androidTester.modalTitle')}
          </h2>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* Success message */}
          {submitStatus === 'success' && (
            <div style={{
              backgroundColor: '#d1fae5',
              color: '#065f46',
              padding: '1.5rem',
              borderRadius: '0.75rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
              <p style={{ margin: 0, fontWeight: '600', fontSize: '1.1rem' }}>
                {t('androidTester.thankYou')}
              </p>
            </div>
          )}

          {/* Recruitment full message */}
          {submitStatus === 'full' && (
            <div style={{
              backgroundColor: '#fef3c7',
              color: '#92400e',
              padding: '1.5rem',
              borderRadius: '0.75rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🙏</div>
              <p style={{ margin: 0, fontWeight: '500' }}>
                {t('androidTester.errorFull')}
              </p>
              <button
                onClick={onClose}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#92400e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                OK
              </button>
            </div>
          )}

          {/* Duplicate email message */}
          {submitStatus === 'duplicate' && (
            <div style={{
              backgroundColor: '#fef3c7',
              color: '#92400e',
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              {t('androidTester.errorDuplicate')}
            </div>
          )}

          {/* Error message */}
          {submitStatus === 'error' && errorMessage && (
            <div style={{
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              {errorMessage}
            </div>
          )}

          {/* Form - show unless success or full */}
          {submitStatus !== 'success' && submitStatus !== 'full' && (
            <>
              <p style={{
                fontSize: '0.95rem',
                color: '#4b5563',
                marginBottom: '1.25rem',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                {t('androidTester.modalDescription')}
              </p>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.25rem'
                  }}>
                    {t('androidTester.nameLabel')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    maxLength={50}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3ddc84'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.25rem'
                  }}>
                    {t('androidTester.emailLabel')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('androidTester.emailPlaceholder')}
                    maxLength={100}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3ddc84'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                <p style={{
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  marginBottom: '1.25rem',
                  textAlign: 'center'
                }}>
                  🔒 {t('androidTester.privacyNote')}
                </p>

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={handleSkip}
                    style={{
                      flex: 1,
                      padding: '0.875rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      backgroundColor: 'white',
                      color: '#6b7280',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {t('androidTester.notInterested')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      flex: 2,
                      padding: '0.875rem',
                      border: 'none',
                      borderRadius: '0.5rem',
                      background: isSubmitting
                        ? '#9ca3af'
                        : 'linear-gradient(135deg, #3ddc84 0%, #1a73e8 100%)',
                      color: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(61, 220, 132, 0.4)'
                    }}
                  >
                    {isSubmitting ? t('androidTester.submitting') : t('androidTester.submitButton')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AndroidTesterModal;
